import { useEffect } from 'react'
import { useTranslation } from '@flight-tools/i18n'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from '@flight-tools/ui'
import { AirplaneTilt } from '@phosphor-icons/react'
import type { AircraftConfig } from '@flight-tools/domain'

import en from './locales/en.json'
import es from './locales/es.json'
import sv from './locales/sv.json'

interface AircraftSelectorProps {
  selectedAircraft: AircraftConfig
}

export function AircraftSelector({ selectedAircraft }: AircraftSelectorProps) {
  const { t, i18n } = useTranslation('aircraft-selector')

  // Load colocated translations
  useEffect(() => {
    i18n.addResourceBundle('en', 'aircraft-selector', en, true, true)
    i18n.addResourceBundle('es', 'aircraft-selector', es, true, true)
    i18n.addResourceBundle('sv', 'aircraft-selector', sv, true, true)
  }, [i18n])

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2">
          <AirplaneTilt weight="duotone" className="h-6 w-6 text-primary" />
          {t('aircraftSelection')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Aircraft info display */}
        <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/20 p-3">
          <span className="font-semibold text-primary">{selectedAircraft.airline}</span>
          <span className="text-muted-foreground">—</span>
          <span>{selectedAircraft.model}</span>
        </div>
        
        {/* Aircraft details */}
        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
          <div className="rounded-lg bg-muted/30 p-3">
            <div className="text-muted-foreground">{t('businessCapacity')}</div>
            <div className="text-lg font-bold text-primary">
              {selectedAircraft.businessRows * selectedAircraft.businessSeatsPerRow}
              <span className="ml-1 text-sm font-normal text-muted-foreground">
                ({selectedAircraft.businessRows} {t('rows')})
              </span>
            </div>
          </div>
          <div className="rounded-lg bg-muted/30 p-3">
            <div className="text-muted-foreground">{t('economyCapacity')}</div>
            <div className="text-lg font-bold">
              {selectedAircraft.economyRows * selectedAircraft.economySeatsPerRow}
              <span className="ml-1 text-sm font-normal text-muted-foreground">
                ({selectedAircraft.economyRows} {t('rows')})
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
